<template>
  <div class="server-side-modal">
    <!-- content that's rendered inside the ssr but not actually shown -->
    <div
      v-if="!modalReady"
      class="d-none"
    >
      <component :is="componentName" />
    </div>

    <!--- modal that gets initialized at mounted() -->
    <modal
      v-if="modalReady"
      :name="modalName"
      :adaptive="true"
      width="100%"
      height="100%"
      :scrollable="scrollable"
      class="ssr-modal"
    >
      <template slot="top-right">
        <button
          class="modal-close"
          type="button"
          @click="close"
        >
          <i
            class="icon"
          >
            <icon-times />
          </i>
        </button>
      </template>

      <!-- listen to emitted "closeModal" from component in modal to close modal ^^-->
      <component
        :is="componentName"
        :modal-name="modalName"
        @closeModal="close"
      />
    </modal>
  </div>
</template>

<script>
/**
 * @@author         j0Shi
 * @@date           2019-09-03
 * @@description    adds a wrapper around modal-vs-js that handles all
 *                  necessary stuff to use modals without ssr pain
 * @@usedby         Search.vue
 * @@usage          Include with a ref and show the modal using $refs.xxx.show()
 */
import { mapMutations } from 'vuex';

/* ICONS */
import IconTimes from 'ICON:IconTimes';

export default {
  name: 'ServerSideModal',
  components: {
    IconTimes,
    SearchModalSearch: () => import(/* webpackChunkName: "lazychunk-modal-search" */ 'components/SearchModalSearch.vue'),
  },
  props: {
    // name of the modal - this is internally used to show the modal
    modalName: {
      type: String,
      default: 'modal',
    },
    // path to the component inside the modal
    componentName: {
      type: String,
      required: true,
    },
    // set this to true if you want the modal to open instantly
    isInitiallyOpen: {
      type: Boolean,
      default: false,
    },
    scrollable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      // @vuese
      // used to make sure the modal isn't initialized before mounted()
      // @type Boolean
      modalReady: false,
    };
  },
  mounted() {
    // make the modal available after ssr
    this.modalReady = true;
    this.$emit('ssr-modal-ready', this.modalName);
    if (this.isInitiallyOpen) this.show();
  },
  methods: {
    ...mapMutations({
      setModalStatus: 'SET_MODAL_STATUS',
    }),

    // @vuese
    // simple method to show the modal
    // should be accessed by parent using ref
    show() {
      // eslint-disable-next-line no-console
      this.$modal.show(this.modalName);
      // emits the current status (open / close) of the modal
      this.$emit('modal-status', true);
    },
    // @vuese
    // simple method to close the modal
    // should be accessed by parent using ref
    close() {
      this.$modal.hide(this.modalName);
      this.setModalStatus({ status: false });
      this.$emit('modal-status', false);
    },
  },
};
</script>

<style lang="scss" scoped>
/deep/ .ssr-modal > *{
  top: 0 !important;
  overflow-y: auto;

  /*      padding: $margin_content*2 $margin_content $margin_content; */
}

/deep/ .ssr-modal{
  div[data-modal]{
    position: static;
    box-sizing: border-box;
    left: 0;
    top: 0;
    width: 100%;
    height: auto;
    background: none;
    z-index: 1;
    opacity: 1;
  }
}
</style>
